import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { countBy, isEmpty, mapValues, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types/pusher';
import { Box, Page, Button, Spinner, Switch, TableList, Typography, useStateInRef, PageEmptyState, PageErrorState, PageContentHeader, ConfirmationModal, useOverlayContext, FilterPickerGroup, Row, useDevice, DropdownMenu, DropdownMenuItem } from 'seagull';
import { ChevronDown, Download, Import, Settings } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { BulkDraftAgentPane } from '~/components/ai/BulkDraftAgentPane/BulkDraftAgentPane';
import ConfirmExternallySignModal from '~/components/ConfirmExternallySignModal/ConfirmExternallySignModal';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import TableFilters from '~/components/TableFilters/TableFilters';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useCreateRelationshipFromDraft } from '~/hooks/useCreateRelationshipFromDraft';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ErrorResponse, errorTextsPath } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { useBusinessChannelEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import { VisibleColumnsPicker } from '../../components/VisibleColumnsPicker';
import BulkActions from './BulkActions';
import { DraftsAiSearch } from './DraftsAiSearch/DraftsAiSearch';
import { useDraftsAiSearchResults } from './DraftsAiSearch/hooks/useDraftsAiSearchResults';
import { useDraftsAiSearchStore } from './DraftsAiSearch/hooks/useDraftsAiSearchStore';
import styles from './DraftsDashboard.module.scss';
import { bulkExternallySignErrorText, deriveActiveFilterText, encodeFilterToUrl, filterAndSortDrafts, readBulkPaneOpenFromLocationState, readFilterFromUrl, readSelectedDraftIdsFromLocationState, selectedDraftNames, selectedDraftNamesById, selectedRowsFromDraftIds } from './DraftsDashboard.utils';
import DraftsDashboardRow from './DraftsDashboardRow';
import EditDraftsTable from './EditDraftsTable/EditDraftsTable';
import { EditDraftsTableColumns } from './EditDraftsTable/types';
import { EmptyStateIllustration } from './EmptyStateIllustration';
import { useDrafts } from './hooks/useDrafts';
export const headerIds = [
    'Company name',
    'Modified by',
    'Last modified'
];
export const GetDraftsDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            DraftsDashboardTID: {
                Root: 'drafts-dashboard',
                ErrorState: 'error-state-page',
                EmptyState: 'empty-state-page',
                RemoveModalTemplateName: 'drafts-dashboard-remove-modal-template-name',
                CloseImportModal: 'close-import-agreements-modal',
                AiSearch: 'drafts-ai-search',
                AiSearchDimOverlay: 'drafts-dashboard-ai-search-dim-overlay',
                TableHeader: 'drafts-dashboard-table-header',
                TableLoadingSpinner: 'drafts-dashboard-table-loading-spinner',
                EmptySearchClearButton: 'drafts-dashboard-empty-search-clear-btn',
                Search: 'drafts-dashboard-search',
                MoreActions: {
                    Menu: 'drafts-dashboard-more-actions',
                    Button: 'drafts-dashboard-more-actions-btn',
                    Import: 'import-drafts-btn',
                    ExportAll: 'export-all-drafts-btn'
                },
                EmptyStateNewButton: 'drafts-empty-state-new-btn',
                InlineEditModeSwitch: 'inline-edit-mode-switch',
                EditColumns: 'edit-columns'
            }
        }
    });
export const DraftsDashboardTestIds = getTestIds('DraftsDashboard');
const columnDefs = [
    {
        width: [
            180,
            400
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_drafts_by_company_name'
        }
    },
    {
        width: [
            120,
            180
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_drafts_by_modified_by'
        }
    },
    {
        width: 110,
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_drafts_by_last_modified'
        }
    }
];
function getTableDefs(texts) {
    return [
        {
            headerItem: {
                id: 'Company name',
                value: texts.companyName()
            },
            sortable: true,
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: 'Modified by',
                value: texts.modifiedBy()
            },
            sortable: true,
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: 'Last modified',
                value: texts.lastModified()
            },
            sortable: true,
            defaultSortDirection: 'desc'
        }
    ];
}
function ActualDraftsDashboard(props) {
    const { data, isRefreshing, refresh, aiSearchEnabled, aiSearchPlaceholder, aiSearchTotalDraftCount, aiSearchTotalMatchCount, isAiSearchSubmitting, isAiSearchLoading } = props;
    const { t: draftsTexts } = useLocaleKeys('pages.draftsDashboard');
    const { DraftsDashboardTID } = useMemo(()=>GetDraftsDashboardTID(), []);
    const { t: commonTexts } = useLocaleKeys('common');
    const { UPDATE_DEFAULT_AGREEMENT_TITLE, BULK_AGENTIC_DRAFTS } = useFeatureFlags();
    const defaultAgreementTitle = UPDATE_DEFAULT_AGREEMENT_TITLE ? commonTexts.defaultAgreementTitle() : commonTexts.untitledAgreement();
    const { t: editTableTexts } = useLocaleKeys('pages.draftsDashboard.editTable');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const columnsTexts = editTableTexts.columns;
    const title = draftsTexts.title();
    const { t: importModalTexts } = useLocaleKeys('pages.agreementsDashboard.importModal');
    const tableDefs = useMemo(()=>getTableDefs(draftsTexts), []);
    const headerItems = useMemo(()=>tableDefs.map((d)=>d.headerItem), [
        tableDefs
    ]);
    const sortOptions = useMemo(()=>tableDefs.flatMap((d, colIndex)=>d.sortable ? [
                {
                    id: d.headerItem.id,
                    label: d.headerItem.value,
                    defaultDirection: d.defaultSortDirection,
                    colIndex
                }
            ] : []), [
        tableDefs
    ]);
    const [sort, setSort] = useState({
        colIndex: 2,
        sort: 'desc'
    });
    const { api, logBI } = useStoreActions();
    // eslint-disable-next-line no-restricted-syntax -- WHY: drives show/hide of more-actions + advanced-edit toggle on mobile, not a Row->Column layout swap, so the rule's primitive-swap remedy does not apply
    const { isMobileDevice } = useDevice();
    const { browser, getNow } = useIoContext();
    const { downloadCsv } = browser;
    const overlayContext = useOverlayContext();
    const createRelationshipFromDraft = useCreateRelationshipFromDraft();
    const isVendor = useStore(selectors.user.isVendor);
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11352);
    const [selected, setSelected] = useState(()=>selectedRowsFromDraftIds(readSelectedDraftIdsFromLocationState(browser.readPersistedLocationState())));
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const [bulkDraftAgentPaneOpen, setBulkDraftAgentPaneOpen] = useState(()=>readBulkPaneOpenFromLocationState(browser.readPersistedLocationState()));
    const setBulkPaneOpen = (open)=>{
        setBulkDraftAgentPaneOpen(open);
        browser.persistLocationState({
            bulkPaneOpen: open
        });
    };
    const restoreScope = (scope)=>{
        var _countBy_true;
        if (scope.currentFilter != null && useDraftsAiSearchStore.getState().filter == null) {
            useDraftsAiSearchStore.getState().setFilter(scope.currentFilter, '');
        }
        if (scope.selectedDraftIds != null && ((_countBy_true = countBy(getSelected()).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0) === 0) {
            setSelected(selectedRowsFromDraftIds(scope.selectedDraftIds));
        }
    };
    const showBulkAgent = BULK_AGENTIC_DRAFTS && !isMobileDevice;
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const { doBulkAction } = useBulkActionModal();
    const hasDrafts = data && data.length > 0;
    const [exportingAllCsv, setExportingAllCsv] = useState(false);
    const [exportingBulkCsv, setExportingBulkCsv] = useState(false);
    const [changedDraftIds, setChangedDraftIds] = useState(()=>new Set());
    const [visibleColumns, setVisibleColumns] = useState({
        [EditDraftsTableColumns.PrimaryContact]: true,
        [EditDraftsTableColumns.AdditionalRecipients]: true,
        [EditDraftsTableColumns.AgreementName]: true,
        [EditDraftsTableColumns.EffectiveDate]: true,
        [EditDraftsTableColumns.ModifiedBy]: true,
        [EditDraftsTableColumns.LastModified]: true,
        [EditDraftsTableColumns.ExpirationDate]: true,
        [EditDraftsTableColumns.IntroductoryMessage]: true,
        [EditDraftsTableColumns.TermsOfService]: true,
        [EditDraftsTableColumns.AgreementNote]: true,
        [EditDraftsTableColumns.AutoApproveAmendments]: true,
        [EditDraftsTableColumns.AutoApproveOutOfScopeCharges]: true,
        [EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue]: true,
        [EditDraftsTableColumns.PaymentMethodRequiredForSigning]: true,
        [EditDraftsTableColumns.CreditCardProcessingFees]: true,
        [EditDraftsTableColumns.ManualApprovalOfEachPayment]: true
    });
    const { urlState: { search, edit, filter: urlFilter, q: urlQuery }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        edit: {
            defaultValue: ''
        },
        filter: {
            defaultValue: ''
        },
        q: {
            defaultValue: ''
        }
    });
    const isInlineEditMode = !isMobileDevice && edit === 'table';
    const enableInlineEditMode = ()=>changeUrlState({
            edit: 'table'
        });
    const toggleInlineEditMode = ()=>changeUrlState({
            edit: isInlineEditMode ? '' : 'table'
        });
    const aiBarActive = (aiSearchEnabled !== null && aiSearchEnabled !== void 0 ? aiSearchEnabled : false) && !isMobileDevice;
    const aiSearchFilter = useDraftsAiSearchStore((s)=>s.filter);
    const aiSearchLastQuery = useDraftsAiSearchStore((s)=>s.lastQuery);
    const aiSearchFilterEdited = useDraftsAiSearchStore((s)=>s.filterEditedSinceLastSearch);
    const clearAiFilter = useDraftsAiSearchStore((s)=>s.clearFilter);
    const aiFilterActive = aiBarActive && aiSearchFilter != null;
    const effectiveSearch = aiBarActive ? '' : search;
    const aiFilteredIds = useMemo(()=>aiFilterActive ? (data !== null && data !== void 0 ? data : []).map((draft)=>draft.id) : undefined, [
        aiFilterActive,
        data
    ]);
    const anchorSampleLabel = editTableTexts.anchorSample();
    const { drafts, draftsMap } = useMemo(()=>filterAndSortDrafts(data, effectiveSearch, sort, anchorSampleLabel), [
        data,
        sort,
        effectiveSearch,
        anchorSampleLabel
    ]);
    const selectedDraftIds = getSelectedDrafts();
    const bulkDraftNames = selectedDraftNames(selectedDraftIds, draftsMap, anchorSampleLabel, editTableTexts.unnamedDraft());
    const bulkDraftNamesById = selectedDraftNamesById(selectedDraftIds, draftsMap, anchorSampleLabel, editTableTexts.unnamedDraft());
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: drafts || [],
        scrollElRef
    });
    const editDraftsTableRef = useRef(null);
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(drafts.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        drafts
    ]);
    useEffect(()=>{
        // advanced-edit's drafts come from a separate fetch absent from `drafts`; pruning here would wrongly clear advanced-only selections
        if (!isInlineEditMode && drafts && !isEmpty(getSelected())) {
            const ids = drafts.map((draft)=>draft.id);
            const newSelected = mapValues(getSelected(), (origValue, draftId)=>ids.includes(draftId) ? origValue : false);
            setSelected(newSelected);
        }
    }, [
        drafts,
        isInlineEditMode
    ]);
    useEffect(()=>{
        const selectedDraftIds = Object.keys(selected).filter((id)=>selected[id]);
        browser.persistLocationState({
            selectedDraftIds
        });
    }, [
        selected
    ]);
    useOnMount(()=>{
        // WHY: URL→store is one-way hydration for a fresh load; on a mid-session remount (a filter-change refetch unmounts+remounts this subtree) the URL lags the store, so restoring would clobber the just-applied filter (ANCR-3436)
        const alreadyFiltered = useDraftsAiSearchStore.getState().filter != null;
        const restored = aiBarActive && !alreadyFiltered ? readFilterFromUrl(urlFilter) : null;
        if (restored != null) {
            useDraftsAiSearchStore.getState().setFilter(restored, urlQuery);
        }
    });
    useEffect(()=>{
        if (aiBarActive) {
            const currentFilter = useDraftsAiSearchStore.getState().filter;
            changeUrlState(currentFilter != null ? {
                filter: encodeFilterToUrl(currentFilter),
                q: useDraftsAiSearchStore.getState().lastQuery,
                search: ''
            } : {
                filter: '',
                q: ''
            });
        }
    }, [
        aiSearchFilter,
        aiSearchLastQuery,
        aiBarActive
    ]);
    function getSelectedDrafts() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [draftId, _] = param;
            return draftId;
        });
    }
    async function bulkSend() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            confirmation: {
                title: draftsTexts.sendProposalsInBulkTitle(),
                primaryButtonLabel: draftsTexts.sendProposalsButton(),
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                draftsTexts.sendProposalsConfirmationPrefix(),
                                ' ',
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: DraftsDashboardTID.RemoveModalTemplateName(),
                                    as: "span",
                                    weight: "bold",
                                    children: selectedDrafts.length
                                }),
                                ' ',
                                draftsTexts.sendProposalsConfirmationSuffix()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: draftsTexts.considerSending()
                        })
                    ]
                })
            },
            progressTitle: draftsTexts.sendProposalsInBulkTitle(),
            actionName: draftsTexts.sendProposalsActionName(),
            itemName: draftsTexts.sendProposalsItemName(),
            errorExtra: draftsTexts.sendProposalsErrorExtra(),
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(createRelationshipFromDraft(draftId).catch((err)=>{
                    var _draft_contactDetails;
                    const draft = drafts.find((draft)=>draft.id === draftId);
                    const email = draft === null || draft === void 0 ? void 0 : (_draft_contactDetails = draft.contactDetails) === null || _draft_contactDetails === void 0 ? void 0 : _draft_contactDetails.email;
                    const errorText = new ErrorResponse(err).createRelationshipErrorText(errorTexts);
                    const draftInfo = "".concat(draft ? draft.title : draftsTexts.draftFallback()).concat(email ? " (".concat(email, ")") : '');
                    throw "".concat(draftInfo, " - ").concat(errorText.text);
                })),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        isInlineEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    async function bulkExternallySign() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            confirmation: {
                renderModal: (p)=>/*#__PURE__*/ _jsx(ConfirmExternallySignModal, _object_spread_props(_object_spread({}, p), {
                        bulkData: {
                            numClients: selectedDrafts.length
                        }
                    }))
            },
            progressTitle: draftsTexts.approveOnBehalfProgressTitle(),
            actionName: draftsTexts.approveOnBehalfActionName(),
            progressAction: draftsTexts.approveOnBehalfAction(),
            itemName: draftsTexts.approveOnBehalfItemName(),
            errorExtra: draftsTexts.approveOnBehalfErrorExtra(),
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(api.relationship.createFromDraft(draftId, {
                    externallySign: true
                })),
            getErrorText: (draftId, error)=>bulkExternallySignErrorText(draftsMap.get(draftId), error, defaultAgreementTitle, errorTexts, editTableTexts),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        isInlineEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    async function bulkDiscard() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            progressAction: draftsTexts.discardAction(),
            confirmation: {
                title: draftsTexts.discardDraftsTitle(),
                primaryButtonLabel: draftsTexts.discardButton(),
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                draftsTexts.areYouSureDiscardDraftsPrefix(),
                                ' ',
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: DraftsDashboardTID.RemoveModalTemplateName(),
                                    as: "span",
                                    weight: "bold",
                                    children: selectedDrafts.length
                                }),
                                ' ',
                                draftsTexts.areYouSureDiscardDraftsSuffix({
                                    count: selectedDrafts.length
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: draftsTexts.thisActionCannotBeUndone()
                        })
                    ]
                })
            },
            progressTitle: draftsTexts.discardDraftsTitle(),
            actionName: draftsTexts.discardActionName(),
            itemName: draftsTexts.discardItemName(),
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(api.draft.deleteById(draftId)),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        isInlineEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    const onSortChange = (sort)=>{
        logBI(11350, {
            column: sort ? headerIds[sort.colIndex] : 'none'
        });
        setSort(sort);
    };
    const onClearSearch = ()=>{
        aiFilterActive && clearAiFilter();
        changeUrlState({
            search: ''
        });
        !isInlineEditMode && debouncedLogSearchToBI.onClear();
    };
    const openImportDraftsModal = ()=>{
        overlayContext.showModal({
            biName: 'drafts_dashboard_import_modal_confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'drafts_confirm'
                        },
                        secondaryButton: {
                            name: 'drafts_cancel'
                        }
                    },
                    textAlignCenter: false,
                    primaryButtonLabel: importModalTexts.close(),
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: importModalTexts.title(),
                    primaryButtonTestId: DraftsDashboardTID.CloseImportModal(),
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: importModalTexts.intro()
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        bi: {
                                            name: 'import_drafts_complete_form'
                                        },
                                        children: importModalTexts.completeForm()
                                    }),
                                    ' ',
                                    importModalTexts.outro()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                className: styles.importModalNote,
                                children: importModalTexts.note()
                            })
                        ]
                    })
                });
            }
        });
    };
    const updateVisibleColumns = (columns)=>{
        setVisibleColumns(columns);
    };
    async function exportAllDraftsCsv() {
        if (exportingAllCsv) return;
        setExportingAllCsv(true);
        try {
            const csvString = await api.draft.exportAllDraftsCsv();
            downloadCsv(csvString, "anchor_drafts_export_".concat(format(getNow(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            overlayContext.showErrorToast(draftsTexts.exportError(), 'export-drafts-csv-error');
        } finally{
            setExportingAllCsv(false);
        }
    }
    async function bulkExportDraftsCsv() {
        if (exportingBulkCsv) return;
        setExportingBulkCsv(true);
        const ids = getSelectedDrafts();
        try {
            const csvString = await api.draft.exportDraftsCsvByIds(ids);
            downloadCsv(csvString, "anchor_drafts_export_".concat(format(getNow(), 'yyyy-MM-dd'), ".csv"));
        } catch (unused) {
            overlayContext.showErrorToast(draftsTexts.exportError(), 'export-drafts-csv-error');
        } finally{
            setExportingBulkCsv(false);
        }
    }
    const renderMoreActionsDropdown = ()=>/*#__PURE__*/ _jsxs(DropdownMenu, {
            testId: DraftsDashboardTID.MoreActions.Menu(),
            target: /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'open_drafts_more_actions'
                },
                skin: "primaryOutline",
                testId: DraftsDashboardTID.MoreActions.Button(),
                suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                children: draftsTexts.moreActions()
            }),
            children: [
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: DraftsDashboardTID.MoreActions.Import(),
                    iconStart: /*#__PURE__*/ _jsx(Download, {}),
                    bi: {
                        name: 'import_drafts'
                    },
                    onClick: openImportDraftsModal,
                    children: draftsTexts.importButton()
                }),
                hasDrafts && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: DraftsDashboardTID.MoreActions.ExportAll(),
                    iconStart: /*#__PURE__*/ _jsx(Import, {}),
                    bi: {
                        name: 'export_drafts'
                    },
                    onClick: exportAllDraftsCsv,
                    disabled: exportingAllCsv,
                    intent: Intent.ExportDraftsToCsv,
                    children: draftsTexts.exportAll()
                })
            ]
        });
    const renderEmptyState = ()=>/*#__PURE__*/ _jsx(PageEmptyState, {
            maxHeight: true,
            testId: DraftsDashboardTID.EmptyState(),
            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
            title: draftsTexts.noDraftsYet.title(),
            message: draftsTexts.noDraftsYet.message(),
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    !isMobileDevice && renderMoreActionsDropdown(),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {
                        testId: DraftsDashboardTID.EmptyStateNewButton()
                    })
                ]
            })
        });
    const headerActions = !isMobileDevice ? /*#__PURE__*/ _jsxs(Row, {
        gap: 16,
        alignItems: "center",
        className: styles.headerActions,
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.inlineEditMode,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: draftsTexts.inlineEditMode()
                    }),
                    /*#__PURE__*/ _jsx(Switch, {
                        bi: {
                            name: 'advanced_edit_mode'
                        },
                        testId: DraftsDashboardTID.InlineEditModeSwitch(),
                        checked: isInlineEditMode,
                        onChange: toggleInlineEditMode
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(BulkActions, {
                numSelected: numSelected,
                bulkActionsOpen: bulkActionsOpen,
                setBulkActionsOpen: setBulkActionsOpen,
                bulkSend: bulkSend,
                bulkExternallySign: bulkExternallySign,
                bulkDiscard: bulkDiscard,
                bulkExport: bulkExportDraftsCsv,
                bulkExportDisabled: exportingBulkCsv,
                onEditWithAI: ()=>setBulkPaneOpen(true)
            }),
            isInlineEditMode && /*#__PURE__*/ _jsx(VisibleColumnsPicker, {
                fixedColumns: {
                    [EditDraftsTableColumns.PrimaryContact]: true
                },
                visibleColumns: visibleColumns,
                onApply: updateVisibleColumns,
                testId: DraftsDashboardTID.EditColumns(),
                title: draftsTexts.editColumns(),
                type: "button",
                prefixIcon: /*#__PURE__*/ _jsx(Settings, {}),
                getColumnLabel: (columnName)=>columnsTexts[columnName]()
            })
        ]
    }) : null;
    const renderDraftsTable = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx(FilterPickerGroup, {
                        className: styles.filterPickerGroup,
                        children: aiBarActive ? /*#__PURE__*/ _jsx(DraftsAiSearch, {
                            testId: DraftsDashboardTID.AiSearch(),
                            placeholder: aiSearchPlaceholder !== null && aiSearchPlaceholder !== void 0 ? aiSearchPlaceholder : '',
                            totalDraftCount: aiSearchTotalDraftCount !== null && aiSearchTotalDraftCount !== void 0 ? aiSearchTotalDraftCount : 0,
                            totalMatchCount: aiSearchTotalMatchCount !== null && aiSearchTotalMatchCount !== void 0 ? aiSearchTotalMatchCount : 0,
                            isInlineEditMode: isInlineEditMode,
                            initialQuery: search,
                            onSubstringChange: (value)=>changeUrlState({
                                    search: value
                                }),
                            onEnableAdvancedEdit: enableInlineEditMode,
                            searchRowActions: headerActions
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: "drafts-dashboard",
                                    filterLabels: {},
                                    filterConfig: {},
                                    selectedFilters: {},
                                    setSelectedFilters: noop,
                                    onRefresh: (_filters, newSort)=>{
                                        if (newSort) setSort(newSort);
                                    },
                                    search: {
                                        bi: {
                                            name: 'drafts_search'
                                        },
                                        value: search,
                                        testId: DraftsDashboardTID.Search(),
                                        registerHotkey: true,
                                        onChange: (event)=>{
                                            const searchStr = event.target.value;
                                            changeUrlState({
                                                search: searchStr
                                            });
                                            !isInlineEditMode && debouncedLogSearchToBI.onChange(searchStr);
                                        },
                                        onClear: onClearSearch
                                    },
                                    mobile: {
                                        sortOptions
                                    }
                                }),
                                headerActions
                            ]
                        })
                    })
                }),
                isInlineEditMode ? /*#__PURE__*/ _jsx(EditDraftsTable, {
                    visibleColumns: visibleColumns,
                    ref: editDraftsTableRef,
                    search: search,
                    selected: selected,
                    setSelected: (selected)=>setSelected(selected),
                    aiFilteredIds: aiFilteredIds,
                    changedDraftIds: changedDraftIds,
                    onHighlightEnd: clearDraftHighlight
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(PageContentHeader, {
                            children: /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: DraftsDashboardTID.TableHeader()
                            })
                        }),
                        drafts.length ? paginatedData.map((d, i)=>/*#__PURE__*/ _jsx(DraftsDashboardRow, {
                                draft: d,
                                index: i,
                                refresh: refresh,
                                highlighted: changedDraftIds.has(d.id),
                                onHighlightEnd: clearDraftHighlight
                            }, d.id)) : isAiSearchLoading ? /*#__PURE__*/ _jsx(Spinner, {
                            centeredInBox: true,
                            centerBoxHeight: "100%",
                            testId: DraftsDashboardTID.TableLoadingSpinner()
                        }) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                            title: draftsTexts.noDraftsMatchingFilters.title(),
                            message: draftsTexts.noDraftsMatchingFilters.message(),
                            actions: /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'clear_drafts_search'
                                },
                                testId: DraftsDashboardTID.EmptySearchClearButton(),
                                onClick: onClearSearch,
                                children: draftsTexts.clearSearch()
                            })
                        })
                    ]
                })
            ]
        });
    useBusinessChannelEvent({
        source: 'scout',
        eventName: PusherEventName.RelationshipCreatedFromScheduledDraft,
        callback: refresh
    });
    const onAgentDraftsChanged = async (appliedIds)=>{
        var _editDraftsTableRef_current;
        await refresh();
        isInlineEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
        if (appliedIds != null && appliedIds.length > 0) {
            // WHY: union, not replace — the generative lane fires this once per apply (a single id at a time), so
            // each applied row must stay highlighted as the walk advances; the end recap re-adds the same set.
            setChangedDraftIds((prev)=>new Set([
                    ...prev,
                    ...appliedIds
                ]));
        }
    };
    const clearDraftHighlight = useCallback((draftId)=>{
        setChangedDraftIds((prev)=>{
            if (!prev.has(draftId)) return prev;
            const next = new Set(prev);
            next.delete(draftId);
            return next;
        });
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            showBulkAgent && /*#__PURE__*/ _jsx(BulkDraftAgentPane, {
                testId: DraftsDashboardTestIds.BulkDraftAgentPane(),
                scope: {
                    currentFilter: aiSearchFilter,
                    selectedDraftIds,
                    matchedDraftCount: selectedDraftIds.length,
                    draftNamesById: bulkDraftNamesById,
                    activeFilterText: deriveActiveFilterText(aiSearchLastQuery, aiSearchFilterEdited)
                },
                draftNames: bulkDraftNames,
                onFilterUpdate: (filter)=>useDraftsAiSearchStore.getState().setFilter(filter, ''),
                onSelectionUpdate: (ids)=>setSelected(selectedRowsFromDraftIds(ids)),
                onScopeRestore: restoreScope,
                onDraftsChanged: onAgentDraftsChanged,
                isOpen: bulkDraftAgentPaneOpen,
                onOpenChange: setBulkPaneOpen
            }),
            /*#__PURE__*/ _jsx(TableList.Provider, {
                hasActionColumn: true,
                enableRowSelection: true,
                selectedRows: selected,
                columnDefs: columnDefs,
                sort: sort,
                onSortChange: onSortChange,
                onSelectionChange: setSelected,
                numRows: drafts.length,
                numSelectedRows: numSelected,
                onToggleAllSelection: onToggleAllSelection,
                children: /*#__PURE__*/ _jsx(Page, {
                    testId: DraftsDashboardTID.Root(),
                    variant: isInlineEditMode || !hasDrafts ? 'containedHeight' : 'default',
                    ref: scrollElRef,
                    header: title,
                    actions: hasDrafts && isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            renderMoreActionsDropdown(),
                            /*#__PURE__*/ _jsx(CreateProposalButton, {})
                        ]
                    }),
                    loading: isRefreshing,
                    onScroll: onScroll,
                    children: /*#__PURE__*/ _jsx(Box, {
                        testId: isAiSearchSubmitting ? DraftsDashboardTID.AiSearchDimOverlay() : undefined,
                        className: cn(styles.aiSearchOverlay, {
                            [styles.dimmed]: isAiSearchSubmitting
                        }),
                        children: hasDrafts || aiFilterActive ? renderDraftsTable() : renderEmptyState()
                    })
                })
            })
        ]
    });
}
export function DraftsDashboard() {
    const { t } = useLocaleKeys('pages.draftsDashboard');
    const { DraftsDashboardTID } = useMemo(()=>GetDraftsDashboardTID(), []);
    const title = t.title();
    const { ENABLE_DRAFTS_AI_SEARCH } = useFeatureFlags();
    const aiSearchSubmitting = useDraftsAiSearchStore((s)=>s.isSubmitting);
    const { drafts, isLoading: draftsLoading, isFetching: draftsFetching, refresh: refreshDrafts, error: draftsError } = useDrafts({
        refreshOnHookMount: true
    });
    const { drafts: searchResultDrafts, isActive: aiSearchActive, isFetching: searchFetching, refresh: refreshSearch, totalMatchCount: aiTotalMatchCount } = useDraftsAiSearchResults({
        enabled: ENABLE_DRAFTS_AI_SEARCH
    });
    const { contacts, error: contactsError, isLoading: contactsLoading, refresh: fetchContacts } = useContacts({
        refreshOnMount: true
    });
    useEffect(()=>{
        return ()=>{
            if (ENABLE_DRAFTS_AI_SEARCH) {
                useDraftsAiSearchStore.getState().clearFilter();
            }
        };
    }, [
        ENABLE_DRAFTS_AI_SEARCH
    ]);
    const usingSearchResults = aiSearchActive;
    const effectiveDrafts = usingSearchResults ? searchResultDrafts : drafts;
    const isAiSearchLoading = usingSearchResults && searchFetching && searchResultDrafts.length === 0;
    const fetchData = ()=>{
        return Promise.all([
            fetchContacts(),
            usingSearchResults ? refreshSearch() : refreshDrafts()
        ]);
    };
    const draftsWithContactData = useMemo(()=>{
        const contactMap = Object.fromEntries((contacts !== null && contacts !== void 0 ? contacts : []).map((contact)=>[
                contact.id(),
                {
                    firstName: contact.firstName(),
                    lastName: contact.lastName(),
                    email: contact.email(),
                    companyName: contact.companyName()
                }
            ]));
        return effectiveDrafts.map((draft)=>{
            var _draft_clientContactId;
            return _object_spread_props(_object_spread({}, draft), {
                contactDetails: contactMap[(_draft_clientContactId = draft.clientContactId) !== null && _draft_clientContactId !== void 0 ? _draft_clientContactId : '']
            });
        });
    }, [
        effectiveDrafts,
        contacts
    ]);
    useSetDocumentTitle(title);
    const isLoading = draftsLoading || contactsLoading;
    const isFetching = (usingSearchResults ? searchFetching : draftsFetching) || contactsLoading;
    const error = !usingSearchResults && draftsError || contactsError;
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: DraftsDashboardTID.ErrorState(),
            title: title,
            onRefresh: fetchData
        });
    } else if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: true
        });
    } else {
        var _ref;
        return /*#__PURE__*/ _jsx(ActualDraftsDashboard, {
            data: draftsWithContactData,
            isRefreshing: isFetching,
            refresh: fetchData,
            aiSearchEnabled: ENABLE_DRAFTS_AI_SEARCH,
            aiSearchPlaceholder: t.aiSearchPlaceholder(),
            aiSearchTotalDraftCount: (_ref = drafts === null || drafts === void 0 ? void 0 : drafts.length) !== null && _ref !== void 0 ? _ref : 0,
            aiSearchTotalMatchCount: aiTotalMatchCount,
            isAiSearchSubmitting: aiSearchSubmitting,
            isAiSearchLoading: isAiSearchLoading
        });
    }
}
